import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEYS = {
  CNPJ_TOKEN: 'prime_cnpj_ws_token',
  GAMIFICACAO: 'prime_gamificacao_enabled',
} as const;

export interface SystemConfig {
  cnpjToken: string | null;
  gamificacaoEnabled: boolean;
}

export function useSystemConfig() {
  const [config, setConfig] = useState<SystemConfig>({
    cnpjToken: null,
    gamificacaoEnabled: false,
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      console.log('🔄 [useSystemConfig] Carregando token do banco...');
      
      // Buscar token centralizado do Supabase
      const { data: configData, error } = await supabase
        .from('configuracoes_sistema')
        .select('valor')
        .eq('chave', 'integracoes.cnpj_ws.token')
        .single();

      if (error) {
        console.error('❌ [useSystemConfig] Erro ao buscar token:', error);
        
        // Fallback: tentar localStorage (migração temporária)
        const localToken = localStorage.getItem(STORAGE_KEYS.CNPJ_TOKEN);
        if (localToken) {
          console.log('⚠️ [useSystemConfig] Usando token do localStorage (fallback)');
          setConfig({
            cnpjToken: localToken,
            gamificacaoEnabled: false,
          });
        }
      } else {
        // Token do banco (formato JSONB string: "token_value")
        const tokenValue = configData?.valor ? 
          (typeof configData.valor === 'string' ? 
            JSON.parse(configData.valor) : 
            configData.valor) : 
          null;

        console.log('✅ [useSystemConfig] Token carregado do banco:', {
          hasToken: !!tokenValue,
          tokenLength: tokenValue?.length || 0
        });

        setConfig({
          cnpjToken: tokenValue || null,
          gamificacaoEnabled: false,
        });
      }
    } catch (error) {
      console.error('❌ [useSystemConfig] Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações do sistema');
    } finally {
      setIsLoading(false);
    }
  };

  const setCNPJToken = async (token: string) => {
    try {
      const trimmedToken = token.trim();

      if (trimmedToken) {
        console.log('💾 [useSystemConfig] Salvando token no banco...');
        
        // Salvar no banco centralizado (formato JSONB)
        const { error } = await supabase
          .from('configuracoes_sistema')
          .update({ 
            valor: JSON.stringify(trimmedToken),
            updated_at: new Date().toISOString()
          })
          .eq('chave', 'integracoes.cnpj_ws.token');

        if (error) {
          console.error('❌ [useSystemConfig] Erro ao salvar token:', error);
          toast.error('Erro ao salvar token CNPJ.ws no banco');
          return;
        }

        console.log('✅ [useSystemConfig] Token salvo com sucesso no banco');
        
        setConfig(prev => ({ ...prev, cnpjToken: trimmedToken }));
        toast.success('Token CNPJ.ws salvo com sucesso (disponível para todos os usuários)');
        
        // Limpar localStorage antigo (migração)
        localStorage.removeItem(STORAGE_KEYS.CNPJ_TOKEN);
      } else {
        console.log('🗑️ [useSystemConfig] Removendo token do banco...');
        
        // Limpar token do banco
        const { error } = await supabase
          .from('configuracoes_sistema')
          .update({ 
            valor: JSON.stringify(""),
            updated_at: new Date().toISOString()
          })
          .eq('chave', 'integracoes.cnpj_ws.token');

        if (error) {
          console.error('❌ [useSystemConfig] Erro ao remover token:', error);
          toast.error('Erro ao remover token');
          return;
        }

        setConfig(prev => ({ ...prev, cnpjToken: null }));
        toast.success('Token CNPJ.ws removido');
        
        // Limpar localStorage antigo (migração)
        localStorage.removeItem(STORAGE_KEYS.CNPJ_TOKEN);
      }
    } catch (error) {
      console.error('❌ [useSystemConfig] Erro ao salvar token:', error);
      toast.error('Erro ao salvar token CNPJ.ws');
    }
  };

  const setGamificacaoEnabled = (enabled: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEYS.GAMIFICACAO, String(enabled));
      setConfig(prev => ({ ...prev, gamificacaoEnabled: enabled }));
      toast.success(`Gamificação ${enabled ? 'ativada' : 'desativada'}`);
    } catch (error) {
      console.error('❌ Erro ao alterar gamificação:', error);
      toast.error('Erro ao alterar configuração de gamificação');
    }
  };

  const clearConfig = async () => {
    try {
      console.log('🗑️ [useSystemConfig] Limpando configurações...');
      
      // Limpar token do banco
      const { error } = await supabase
        .from('configuracoes_sistema')
        .update({ 
          valor: JSON.stringify(""),
          updated_at: new Date().toISOString()
        })
        .eq('chave', 'integracoes.cnpj_ws.token');

      if (error) {
        console.error('❌ [useSystemConfig] Erro ao limpar token:', error);
        toast.error('Erro ao limpar configurações');
        return;
      }

      // Limpar localStorage antigo (migração)
      localStorage.removeItem(STORAGE_KEYS.CNPJ_TOKEN);
      
      setConfig({
        cnpjToken: null,
        gamificacaoEnabled: false,
      });
      
      toast.success('Configurações limpas com sucesso');
    } catch (error) {
      console.error('❌ [useSystemConfig] Erro ao limpar configurações:', error);
      toast.error('Erro ao limpar configurações');
    }
  };

  const hasValidCNPJToken = () => {
    return config.cnpjToken && config.cnpjToken.trim().length > 0;
  };

  return {
    config,
    isLoading,
    setCNPJToken,
    setGamificacaoEnabled,
    clearConfig,
    hasValidCNPJToken,
    reload: loadConfig,
  };
}